import { useEffect, useState, useRef } from 'react';

import {
  fetchAuthenticatedUser,
  fetchCompetencies,
  fetchQuestions,
  fetchProsperContent,
  fetchTours,
  populateDataDictionary,
  fetchAllCareerPathContent,
  fetchUserInfo,
} from './useBootstrap.util';
import { useAppDispatch, useAppSelector } from '../redux/hooks';

export type UseBootstrapResult = Readonly<{
  isBootstrapped: boolean;
}>;

export const useBootstrap = (): UseBootstrapResult => {
  const dispatch = useAppDispatch();
  const [isBootstrapped, setIsBootstrapped] = useState(false);

  // track with a ref to avoid duplicate calls
  const isBootstrappingRef = useRef(false);

  const user = useAppSelector((state) => state.user.user);

  useEffect(() => {
    async function bootstrap() {
      // If already bootstrapping, don't start again
      if (isBootstrappingRef.current) return;

      isBootstrappingRef.current = true;

      try {
        const competencies = await fetchCompetencies(dispatch);

        if (competencies) {
          await populateDataDictionary(dispatch, competencies);
        }

        const questions = await fetchQuestions(dispatch);
        const careerPathContent = await fetchAllCareerPathContent(dispatch);

        await fetchTours(dispatch);

        const user = await fetchAuthenticatedUser(dispatch);

        if (user) {
          await fetchProsperContent(dispatch);
          await fetchUserInfo({ dispatch, questions, careerPathContent });
        }

        setIsBootstrapped(true);
      } catch (error) {
        console.error('Failed to bootstrap app', error);
        setIsBootstrapped(false);
        // Reset the ref so we can try again if needed
        isBootstrappingRef.current = false;
      } finally {
        isBootstrappingRef.current = false;
      }
    }

    bootstrap();
  }, [dispatch, user?.id]);

  return {
    isBootstrapped,
  };
};
